import { useEffect, useState } from "react";
import axios from "axios";
import { Star, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Carousel recensioni reali Google Business Profile.
 * Mostra le top reviews ≥ 4 stelle delle 3 sedi Colucci.
 *
 * Props:
 *  - limit (default 9)
 *  - sedeSlug (opzionale: filtra per singola sede)
 *  - showHeader (default true): se true mostra titolo + aggregato
 */
export default function GoogleReviewsCarousel({ limit = 9, sedeSlug = null, showHeader = true }) {
  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/gbp/reviews?limit=${limit * 2}`),
          showHeader ? axios.get(`${API}/gbp/aggregate-rating`) : Promise.resolve(null),
        ]);
        if (!mounted) return;
        let revs = r1.data?.reviews || [];
        if (sedeSlug) revs = revs.filter(r => r.sede_slug === sedeSlug);
        setReviews(revs.slice(0, limit));
        if (r2) setAggregate(r2.data);
      } catch (e) {
        // silent fail: recensioni opzionali
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [limit, sedeSlug, showHeader]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-zinc-400" data-testid="reviews-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (reviews.length === 0) return null;

  return (
    <section className="py-12 sm:py-16 bg-[#FCFCFA]" data-testid="google-reviews-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        {showHeader && (
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs text-amber-800 font-medium mb-3">
              <img src="https://www.gstatic.com/images/branding/product/1x/googleg_24dp.png" alt="Google" className="w-3 h-3" />
              Recensioni verificate Google
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#0F1F14] mb-2">
              I nostri clienti dicono
            </h2>
            {aggregate?.available && (
              <div className="inline-flex items-center gap-2 mt-2" data-testid="reviews-aggregate">
                <span className="text-3xl font-bold text-[#163321]">{aggregate.rating}</span>
                <div className="flex flex-col items-start gap-0.5">
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-4 h-4 ${i <= Math.round(aggregate.rating) ? 'text-amber-400 fill-amber-400' : 'text-zinc-300'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-[#4A5E51]">basato su <strong>{aggregate.review_count} recensioni</strong> Google</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mobile: scroll orizzontale; Desktop: 3 colonne */}
        <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-x-auto sm:overflow-visible snap-x snap-mandatory pb-4 sm:pb-0 -mx-5 px-5 sm:mx-0 sm:px-0">
          {reviews.map((r, i) => (
            <ReviewCard key={i} review={r} />
          ))}
        </div>

        <p className="text-[10px] text-[#4A5E51] text-center mt-6 italic">
          Recensioni sincronizzate da Google Maps. Aggiornate automaticamente ogni giorno.
        </p>
      </div>
    </section>
  );
}

function ReviewCard({ review }) {
  return (
    <div
      className="snap-start shrink-0 w-[85%] sm:w-auto bg-white border border-[#E5E2DB] rounded-2xl p-5 hover:shadow-md transition-shadow flex flex-col gap-3"
      data-testid={`review-card-${review.sede_slug}`}
    >
      <div className="flex items-start gap-3">
        {review.profile_photo_url ? (
          <img src={review.profile_photo_url} alt={review.author_name} className="w-10 h-10 rounded-full object-cover shrink-0" loading="lazy" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#163321]/10 text-[#163321] flex items-center justify-center font-semibold text-sm shrink-0">
            {review.author_name?.charAt(0).toUpperCase() || "?"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-[#0F1F14] truncate">{review.author_name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className={`w-3 h-3 ${i <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-zinc-300'}`} />
              ))}
            </div>
            <span className="text-[10px] text-zinc-400">·</span>
            <span className="text-[10px] text-zinc-400">{review.relative_time}</span>
          </div>
        </div>
      </div>
      <p className="text-sm text-[#4A5E51] leading-relaxed line-clamp-5">{review.text}</p>
      <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 mt-auto pt-2 border-t border-[#E5E2DB]/60">
        <MapPin className="w-3 h-3" />
        <span>Sede di {review.sede_label}</span>
      </div>
    </div>
  );
}
